import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  authMe, authLogout, pinLogin,
  register as apiRegister, emailLogin, forgotPassword, resetPassword,
} from "@/lib/api";

const AuthContext = createContext(null);

// NOTE: localStorage here stores ONLY non-sensitive UI preferences
// (the "view-as-role" toggle for Titolare's role simulator).
// The actual authentication session lives in an httpOnly+secure+samesite=none
// cookie set by the backend and is NEVER accessible from JS.
const VIEW_AS_KEY = "edilmind-view-as-role";

export const ROLES = {
  titolare: { label: "Titolare", color: "slate-900", textColor: "slate-50" },
  direttore_lavori: { label: "Direttore Lavori", color: "blue-700", textColor: "blue-50" },
  responsabile_sicurezza: { label: "Responsabile Sicurezza", color: "orange-500", textColor: "orange-50" },
  caposquadra: { label: "Caposquadra", color: "emerald-600", textColor: "emerald-50" },
  operaio: { label: "Operaio", color: "slate-500", textColor: "slate-50" },
};

// Capabilities per role
export const CAPS = {
  titolare: {
    canUpload: true, canDelete: true, canManageReminders: true,
    tabs: ["sommario", "pagamenti", "documenti", "rischi", "piano_azione"],
  },
  direttore_lavori: {
    canUpload: true, canDelete: true, canManageReminders: true,
    tabs: ["sommario", "pagamenti", "documenti", "rischi", "piano_dl"],
  },
  responsabile_sicurezza: {
    canUpload: false, canDelete: false, canManageReminders: false,
    tabs: ["sicurezza"],
  },
  caposquadra: {
    canUpload: false, canDelete: false, canManageReminders: false,
    tabs: ["documenti_lite", "scadenze_lite"],
  },
  operaio: {
    canUpload: false, canDelete: false, canManageReminders: false,
    tabs: ["miei_obblighi"],
  },
};

export const AuthProvider = ({ children }) => {
  // null = checking | false = guest | object = user
  const [user, setUser] = useState(null);
  const [viewAsRole, setViewAsRoleState] = useState(() => {
    try {
      return localStorage.getItem(VIEW_AS_KEY) || null;
    } catch {
      return null;
    }
  });

  const bootstrap = useCallback(async () => {
    try {
      const me = await authMe();
      setUser(me);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (pin, name) => {
    const u = await pinLogin(pin, name);
    setUser(u);
    setViewAsRoleState(null);
    try {
      localStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* noop */
    }
    return u;
  }, []);

  const registerAccount = useCallback(async (data) => {
    const u = await apiRegister(data);
    setUser(u);
    return u;
  }, []);

  const loginAccount = useCallback(async (email, password) => {
    const u = await emailLogin(email, password);
    setUser(u);
    return u;
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    return await forgotPassword(email);
  }, []);

  const submitPasswordReset = useCallback(async (token, password) => {
    const u = await resetPassword(token, password);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch {
      /* noop */
    }
    setUser(false);
    setViewAsRoleState(null);
    try {
      localStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* noop */
    }
  }, []);

  const setViewAsRole = useCallback((role) => {
    setViewAsRoleState(role);
    try {
      if (role) localStorage.setItem(VIEW_AS_KEY, role);
      else localStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* noop */
    }
  }, []);

  // Effective role: if titolare uses "view as", use that; otherwise real role
  const actualRole = user?.role || null;
  const effectiveRole =
    actualRole === "titolare" && viewAsRole && CAPS[viewAsRole] ? viewAsRole : actualRole;

  const value = {
    user,
    isLoading: user === null,
    isAuthenticated: !!user && user !== false,
    actualRole,
    effectiveRole,
    viewAsRole: actualRole === "titolare" ? viewAsRole : null,
    setViewAsRole,
    caps: effectiveRole ? CAPS[effectiveRole] : null,
    roleLabel: effectiveRole ? ROLES[effectiveRole]?.label : null,
    login,
    logout,
    registerAccount,
    loginAccount,
    requestPasswordReset,
    submitPasswordReset,
    refreshUser: bootstrap,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
